/**
 * The primitive components everything else is built from.
 *
 * Small and deliberately unclever: a wallet UI benefits far more from being
 * predictable than from being flexible.
 */

import {
  forwardRef,
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { AlertTriangle, Check, Copy, Info, Loader2, X } from 'lucide-react';
import { cx } from '../lib/format';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: ReactNode;
  full?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brass text-void hover:bg-brass-bright active:bg-brass-deep font-semibold shadow-[0_1px_0_0_rgb(255_255_255/0.2)_inset]',
  secondary:
    'bg-raised text-ink border border-hairline hover:border-hairline-bright hover:bg-hairline',
  ghost: 'text-ink-dim hover:text-ink hover:bg-raised',
  danger: 'bg-loss/12 text-loss border border-loss/30 hover:bg-loss/20',
};

const SIZES = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-12 px-6 text-[15px] gap-2 rounded-xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, full, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // A button mid-request must not be clickable twice — double-broadcasting
      // a transaction is a genuinely bad outcome.
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center whitespace-nowrap transition-colors duration-150',
        'disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        full && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={15} className="animate-spin" /> : icon}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function Panel({
  title,
  action,
  children,
  className,
  padded = true,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={cx('panel', className)}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-hairline px-5 py-3.5">
          {typeof title === 'string' ? (
            <h2 className="text-sm font-semibold text-ink">{title}</h2>
          ) : (
            title
          )}
          {action}
        </header>
      )}
      <div className={cx(padded && 'p-5')}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string | null;
  mono?: boolean;
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, mono, suffix, className, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name ?? label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-[13px] font-medium text-ink-dim">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className={cx(
            'w-full rounded-lg bg-abyss px-3.5 text-sm text-ink transition-colors',
            'border placeholder:text-ink-faint',
            'h-10',
            error ? 'border-loss/60' : 'border-hairline focus:border-brass/60',
            mono && 'font-mono text-[13px]',
            Boolean(suffix) && 'pr-20',
            className,
          )}
          {...rest}
        />
        {suffix && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-ink-faint">
            {suffix}
          </div>
        )}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="text-xs text-loss" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-ink-faint">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'success';
  title?: string;
  children: ReactNode;
}) {
  const tones = {
    info: { ring: 'border-info/30 bg-info/8', text: 'text-info', Icon: Info },
    warn: { ring: 'border-warn/30 bg-warn/8', text: 'text-warn', Icon: AlertTriangle },
    danger: { ring: 'border-loss/35 bg-loss/8', text: 'text-loss', Icon: AlertTriangle },
    success: { ring: 'border-gain/30 bg-gain/8', text: 'text-gain', Icon: Check },
  }[tone];

  return (
    <div className={cx('flex gap-3 rounded-lg border p-3.5', tones.ring)}>
      <tones.Icon size={16} className={cx('mt-0.5 shrink-0', tones.text)} />
      <div className="min-w-0 space-y-1 text-[13px] leading-relaxed">
        {title && <p className={cx('font-semibold', tones.text)}>{title}</p>}
        <div className="text-ink-dim">{children}</div>
      </div>
    </div>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brass' | 'gain' | 'loss' | 'warn';
}) {
  const tones = {
    neutral: 'bg-raised text-ink-dim border-hairline',
    brass: 'bg-brass-wash text-brass border-brass/25',
    gain: 'bg-gain/10 text-gain border-gain/25',
    loss: 'bg-loss/10 text-loss border-loss/25',
    warn: 'bg-warn/10 text-warn border-warn/25',
  }[tone];

  return (
    <span
      className={cx(
        'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-medium',
        tones,
      )}
    >
      {children}
    </span>
  );
}

/** Copy-to-clipboard button with a confirmation state. */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch {
          // The Clipboard API needs a secure context. On a plain-HTTP LAN
          // address it is unavailable, so fall back to a manual selection.
          const node = document.createElement('textarea');
          node.value = value;
          document.body.appendChild(node);
          node.select();
          try {
            document.execCommand('copy');
            setCopied(true);
          } finally {
            document.body.removeChild(node);
          }
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-ink-dim transition-colors hover:bg-raised hover:text-ink"
      aria-label={copied ? 'Copied' : `Copy ${label ?? 'to clipboard'}`}
    >
      {copied ? <Check size={13} className="text-gain" /> : <Copy size={13} />}
      {copied ? 'Copied' : (label ?? 'Copy')}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export function Modal({
  open,
  onClose,
  title,
  children,
  width = 'max-w-md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the dialog from scrolling under it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-void/80 backdrop-blur-sm" onClick={onClose} />
      <div className={cx('panel-raised animate-in relative w-full', width)}>
        <header className="flex items-center justify-between border-b border-hairline px-5 py-3.5">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-ink-faint transition-colors hover:bg-raised hover:text-ink"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading and empty states
// ---------------------------------------------------------------------------

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('shimmer rounded-md', className)} aria-hidden="true" />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icon && <div className="mb-3 text-ink-faint">{icon}</div>}
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-[13px] text-ink-faint">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** A coloured disc bearing a chain's ticker. Cheap, and never a broken image. */
export function ChainAvatar({
  chainId,
  symbol,
  size = 32,
}: {
  chainId: string;
  symbol: string;
  size?: number;
}) {
  const color = chainAvatarColor(chainId);
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full font-semibold"
      style={{
        width: size,
        height: size,
        background: `${color}1f`,
        color,
        border: `1px solid ${color}40`,
        fontSize: size * 0.34,
      }}
      aria-hidden="true"
    >
      {symbol.slice(0, 3).toUpperCase()}
    </div>
  );
}

function chainAvatarColor(chainId: string): string {
  // Kept local to avoid a circular import with format.ts's chainColor.
  let hash = 0;
  for (let i = 0; i < chainId.length; i++) hash = (hash * 31 + chainId.charCodeAt(i)) | 0;
  const known: Record<string, string> = {
    bitcoin: '#f7931a',
    ethereum: '#8a92b2',
    litecoin: '#a5a8a9',
    dogecoin: '#c3a634',
    solana: '#14f195',
    'bitcoin-cash': '#8dc351',
    polygon: '#8247e5',
    base: '#0052ff',
    arbitrum: '#28a0f0',
    cosmos: '#5064fb',
  };
  return known[chainId] ?? `hsl(${Math.abs(hash) % 360} 55% 58%)`;
}
